import { useTranslation } from '@kostbar/i18n'
import type {
  ClosingPaymentRow,
  ClosingRateRow,
  ClosingSheet,
  ClosingSlice,
  ClosingZahlungen,
} from '@/features/reports/api/use-closing-report-query'
import {
  channelLabel,
  paymentMethodLabel,
  spartenLabel,
  vatRateLabel,
} from '@/features/reports/lib/accounting-labels'
import { formatCurrency } from '@/features/reports/lib/format'
import { cn } from '@/lib/utils'

/**
 * The Übersicht's six sections, in the order the controller reads them
 * (CHG-2026-09-tax-agency-report; the RKSV block folded into ZAHLUNGEN with
 * CHG-2026-10-accounting-close-periods, D10).
 *
 * The same order, the same wording and the same figures as the server-rendered PDF: the printout is
 * the document that gets filed, and a screen that arranged it differently would make the reader
 * check twice whether they are looking at the same period.
 *
 * **Two columns, not one stack.** A closing sheet is read as a document: the label and its amount
 * belong on one line the eye can span. Stacked full-width on a wide screen the two ends of a row sat
 * a screen apart, which is what made the first build unreadable — so the sheet takes a document
 * measure (capped on the page) and splits into the canvas's two columns: what the money *is* on the
 * left (Umsatz, Sparten, Kanäle), how it *arrived* on the right (Zahlungen an der Kassa and online)
 * followed by the two blocks that are deliberately not revenue.
 *
 * **No caveat banners anywhere.** Sales without operator data land under "Unbekannt" and say so by
 * being there; the spec forbids explaining that in a banner on a controller-facing report. The only
 * notes on this page are the two "this is not revenue" sentences the law-facing sections need, and
 * the Entnahme value flags, which qualify a figure rather than the report.
 */
export function AccountingSections({ report }: { report: ClosingSheet }) {
  return (
    <div className="grid items-start gap-4 xl:grid-cols-2">
      <div className="flex flex-col gap-4">
        <UmsatzSection report={report} />
        <SliceSection rows={report.sparten} variant="sparten" />
        <SliceSection rows={report.kanaele} variant="channel" />
      </div>
      <div className="flex flex-col gap-4">
        <ZahlungenSection zahlungen={report.zahlungen} />
        <EntnahmenSection report={report} />
        <TrinkgeldSection report={report} />
      </div>
    </div>
  )
}

/** Brutto per rate, net first as on the Beleg, and what of it was sold vor Ort and online. */
export function UmsatzSection({ report }: { report: ClosingSheet }) {
  const { t } = useTranslation('reports')
  return (
    <Section
      title={t('accounting.sections.umsatz')}
      unit={t('accounting.units.amounts')}
    >
      <RateTable
        rates={report.umsatz.rates}
        totalCents={report.umsatz.grossCents}
      >
        <tr className={SUB}>
          <td className={SUB_CELL}>{t('accounting.umsatzVorOrt')}</td>
          <td />
          <td />
          <Money value={report.umsatz.vorOrtCents} />
        </tr>
        <tr className={SUB}>
          <td className={SUB_CELL}>{t('accounting.umsatzOnline')}</td>
          <td />
          <td />
          <Money value={report.umsatz.onlineCents} />
        </tr>
      </RateTable>
    </Section>
  )
}

/** Netto, USt and Brutto per rate in canonical order, closed by the Summe. */
export function RateTable({
  rates,
  totalCents,
  children,
}: {
  rates: Array<ClosingRateRow>
  totalCents: number
  /** Rows under the Summe. */
  children?: React.ReactNode
}) {
  const { t } = useTranslation('reports')
  return (
    <table className={TABLE}>
      <thead>
        <tr className="border-b border-border text-muted-foreground">
          <th scope="col" className={cn(HEAD, 'text-left')}>
            {t('accounting.columns.rate')}
          </th>
          <th scope="col" className={cn(HEAD, 'text-right')}>
            {t('accounting.columns.net')}
          </th>
          <th scope="col" className={cn(HEAD, 'text-right')}>
            {t('accounting.columns.vat')}
          </th>
          <th scope="col" className={cn(HEAD, 'text-right')}>
            {t('accounting.columns.gross')}
          </th>
        </tr>
      </thead>
      <tbody>
        {rates.map((rate) => (
          <tr key={rate.vatCode}>
            <td className={CELL}>{vatRateLabel(t, rate.vatCode)}</td>
            <Money value={rate.netCents} />
            <Money value={rate.vatCents} />
            <Money value={rate.grossCents} />
          </tr>
        ))}
        <tr className={TOTAL}>
          <td className={TOTAL_CELL}>{t('accounting.total')}</td>
          <td />
          <td />
          <Money value={totalCents} total />
        </tr>
        {children}
      </tbody>
    </table>
  )
}

function SliceSection({
  rows,
  variant,
}: {
  rows: Array<ClosingSlice>
  variant: 'sparten' | 'channel'
}) {
  const { t } = useTranslation('reports')
  const total = rows.reduce((sum, row) => sum + row.grossCents, 0)
  return (
    <Section
      title={t(
        variant === 'sparten'
          ? 'accounting.sections.sparten'
          : 'accounting.sections.kanaele',
      )}
      unit={t('accounting.units.amount')}
    >
      <table className={TABLE}>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td className={CELL}>
                {variant === 'sparten'
                  ? spartenLabel(t, row.key, row.label)
                  : channelLabel(t, row.key, row.label)}
              </td>
              <Money value={row.grossCents} />
            </tr>
          ))}
          <tr className={TOTAL}>
            <td className={TOTAL_CELL}>{t('accounting.total')}</td>
            <Money value={total} total />
          </tr>
        </tbody>
      </table>
    </Section>
  )
}

/**
 * How the money arrived, closed against UMSATZ (D12), in two groups (D10): an der Kassa — the RKSV
 * vor-Ort tenders, an online order's counter payment included — and online. Each group leads with
 * its total and carries its methods underneath.
 *
 * The Summe is the payload's `totalCents`, never `sum(rows)`: a filing document may not show two
 * grand totals that silently disagree, so whatever separates the sides is rendered as its own line,
 * named "nicht zugeordnet" rather than left as the gap between two figures the reader would have to
 * subtract themselves. It is zero in a healthy period and renders nothing.
 */
function ZahlungenSection({ zahlungen }: { zahlungen: ClosingZahlungen }) {
  const { t } = useTranslation('reports')
  return (
    <Section
      title={t('accounting.sections.zahlungen')}
      unit={t('accounting.units.amount')}
    >
      <table className={TABLE}>
        <tbody>
          <PaymentGroup
            label={t('accounting.zahlungenKassa')}
            cents={zahlungen.kassaCents}
            rows={zahlungen.kassa}
          />
          <PaymentGroup
            label={t('accounting.zahlungenOnline')}
            cents={zahlungen.onlineCents}
            rows={zahlungen.online}
          />
          {zahlungen.unassignedCents !== 0 ? (
            <tr className="text-muted-foreground">
              <td className={CELL}>{t('accounting.unassigned')}</td>
              <Money value={zahlungen.unassignedCents} />
            </tr>
          ) : null}
          <tr className={TOTAL}>
            <td className={TOTAL_CELL}>{t('accounting.total')}</td>
            <Money value={zahlungen.totalCents} total />
          </tr>
        </tbody>
      </table>
    </Section>
  )
}

function PaymentGroup({
  label,
  cents,
  rows,
}: {
  label: string
  cents: number
  rows: Array<ClosingPaymentRow>
}) {
  const { t } = useTranslation('reports')
  return (
    <>
      <tr className="font-medium">
        <td className="px-4 py-1.5">{label}</td>
        <Money value={cents} />
      </tr>
      {rows.map((row) => (
        <tr key={row.method} className={SUB}>
          <td className={SUB_CELL}>{paymentMethodLabel(t, row.method)}</td>
          <Money value={row.grossCents} />
        </tr>
      ))}
    </>
  )
}

function EntnahmenSection({ report }: { report: ClosingSheet }) {
  const { t } = useTranslation('reports')
  const { entnahmen } = report
  return (
    <Section
      title={t('accounting.sections.entnahmen')}
      unit={t('accounting.units.warenwert')}
      aside
    >
      <p className={NOTE}>{t('accounting.entnahmenNote')}</p>
      <table className={TABLE}>
        <tbody>
          {entnahmen.reasons.map((reason) => (
            <tr key={reason.reasonCode}>
              <td className={CELL}>
                {reason.reasonLabel ?? reason.reasonCode}
              </td>
              <Money value={reason.warenwertCents} />
            </tr>
          ))}
          <tr className={TOTAL}>
            <td className={TOTAL_CELL}>{t('accounting.total')}</td>
            <Money value={entnahmen.warenwertCents} total />
          </tr>
        </tbody>
      </table>
      {entnahmen.approximated ? (
        <p className={FOOT}>{t('accounting.entnahmenApproximated')}</p>
      ) : null}
      {entnahmen.valueMissing ? (
        <p className={FOOT}>{t('accounting.entnahmenValueMissing')}</p>
      ) : null}
    </Section>
  )
}

function TrinkgeldSection({ report }: { report: ClosingSheet }) {
  const { t } = useTranslation('reports')
  const { trinkgeld } = report
  return (
    <Section
      title={t('accounting.sections.trinkgeld')}
      unit={t('accounting.units.amount')}
      aside
    >
      <p className={NOTE}>{t('accounting.trinkgeldNote')}</p>
      <table className={TABLE}>
        <tbody>
          {trinkgeld.byMethod.map((row) => (
            <tr key={row.key}>
              <td className={CELL}>{paymentMethodLabel(t, row.key)}</td>
              <Money value={row.grossCents} />
            </tr>
          ))}
          <tr className={TOTAL}>
            <td className={TOTAL_CELL}>{t('accounting.total')}</td>
            <Money value={trinkgeld.totalCents} total />
          </tr>
        </tbody>
      </table>
      {trinkgeld.recorded ? null : (
        <p className={FOOT}>{t('accounting.trinkgeldNone')}</p>
      )}
    </Section>
  )
}

/**
 * The sheet's row rhythm, in one place.
 *
 * Deliberately dense and rule-free: a closing sheet is scanned column-wise, and a border under every
 * line turns a money list into a settings table. Rules are spent only where they carry meaning — the
 * column header, a subtotal, a total.
 */
export const TABLE = 'w-full text-sm'
export const HEAD = 'h-8 px-4 text-xs font-medium'
export const CELL = 'px-4 py-1'
export const SUB = 'text-[13px] text-muted-foreground'
export const SUB_CELL = 'py-0.5 pl-9 pr-4'
export const TOTAL = 'border-t-2 border-border font-semibold'
export const TOTAL_CELL = 'px-4 py-1.5 pt-2'
export const NOTE = 'px-4 pt-2 text-xs leading-5 text-muted-foreground'
export const FOOT = 'px-4 pb-2 pt-1 text-xs leading-5 text-muted-foreground'

export function Money({
  value,
  total,
  lead,
}: {
  value: number
  total?: boolean
  lead?: boolean
}) {
  return (
    <td
      className={cn(
        'px-4 text-right tabular-nums',
        lead ? 'py-1.5 text-base font-bold' : total ? 'py-1.5 pt-2' : 'py-1',
      )}
      data-print-tight
    >
      {formatCurrency(value)}
    </td>
  )
}

/**
 * One block of the sheet.
 *
 * The title bar carries the unit of the column beneath it on the right — "Betrag (€)",
 * "Warenwert (€)" — because a bare column of numbers in a filing document has to say what it is
 * counting, and the two-column sheet has no room for a header row in every block.
 *
 * `aside` marks the sections that are not revenue — Entnahmen and Trinkgeld — which carry a muted ground and a dashed border so that nobody reading quickly adds them into a
 * total they are deliberately outside of.
 */
export function Section({
  title,
  unit,
  children,
  aside,
  className,
}: {
  title: string
  unit?: string
  children: React.ReactNode
  aside?: boolean
  className?: string
}) {
  return (
    <section
      className={cn(
        'break-inside-avoid overflow-hidden rounded-xl border border-border pb-1',
        aside && 'border-dashed bg-muted/30',
        className,
      )}
      data-print-fit
    >
      <div
        className={cn(
          'flex items-baseline justify-between gap-3 border-b border-border px-4 py-2',
          aside ? 'border-dashed bg-muted/40' : 'bg-muted/50',
        )}
      >
        <h2 className="text-xs font-semibold uppercase tracking-[0.08em]">
          {title}
        </h2>
        {unit ? (
          <span className="text-[11px] font-medium text-muted-foreground">
            {unit}
          </span>
        ) : null}
      </div>
      {children}
    </section>
  )
}
